/**
 * Zustand store for conversation messages (user prompts + assistant responses).
 *
 * Messages arrive via WebSocket events, with request-correlated optimistic
 * user messages used where dispatch would otherwise feel delayed. The store
 * maintains the ordered list rendered by conversation surfaces.
 */

import {
  appendConversationMessages,
  normalizeConversationMessage,
  upsertDurableMessage,
  settleExecutionMessages,
} from "@/lib/durable-conversation-message";
import { create } from "zustand";
import {
  mirroredStore,
  withMutationEmitSuppressed,
} from "@/lib/mirrored-store";
import { normalizeAssistantText } from "@/lib/assistant-text-normalization";
import {
  applyStreamingBatch,
  commitStreamingProgress,
  streamIdentity,
  type PendingStreamUpdate,
} from "@/lib/conversation-streaming-batch";
import { reconcileOptimisticUserMessage } from "@/lib/optimistic-user-message";
import type { ConversationMessage } from "./conversation-message-types";
import { sameSpokenText } from "./conversation-spoken-text";
export type { ConversationMessage } from "./conversation-message-types";

interface ConversationState {
  messages: ConversationMessage[];
  /** Steering receipts whose user row has not arrived yet:
   *  execution id -> the running execution it was steered into. */
  pendingSteered: Record<string, string>;

  upsertDurableMessage: (msg: ConversationMessage) => void;

  /** Append a single message (deduplicates by id). */
  pushMessage: (msg: ConversationMessage) => void;
  /** Append several messages in one store update (deduplicates by id). */
  pushMessages: (msgs: ConversationMessage[]) => void;

  /**
   * Queue a streaming assistant-message update. Calls are coalesced
   * within one animation frame so a token stream produces one render
   * per frame instead of one per token. Each call carries the latest
   * cumulative content for the execution; only the last value per
   * execution survives the coalescing window.
   */
  upsertStreamingAssistantMessage: (
    executionId: string,
    content: string,
    createdAt?: string,
    metadata?: {
      stepId?: string;
      sessionId?: string;
      conversationFrameId?: string;
    },
  ) => void;

  /** Replace a matching live assistant step with its durable activity row. */
  commitStreamingAssistantProgress: (msg: ConversationMessage) => void;

  /** Replace a temporary streaming assistant message with the saved one. */
  finalizeAssistantMessage: (msg: ConversationMessage) => void;

  /** Replace a request-correlated optimistic user message with its saved row. */
  reconcileUserMessage: (msg: ConversationMessage) => void;

  /** Remove a user message whose prompt receipt was rejected. */
  clearOptimisticUserMessage: (clientRequestId: string) => void;

  /** Move an optimistic user message to the request id selected by dispatch. */
  rekeyOptimisticUserMessage: (currentId: string, nextId: string) => void;

  /** Remove a temporary assistant message when the execution does not save one. */
  clearStreamingAssistantMessage: (executionId: string) => void;

  /** Replace all messages (e.g. from initial load). */
  setMessages: (msgs: ConversationMessage[]) => void;
  /** Apply REST hydration without dropping newer live messages. */
  hydrateMessages: (msgs: ConversationMessage[]) => void;

  /** Flag a live tool_call step as failed so the thread shows a red dot. */
  markToolFailed: (executionId: string, toolUseId: string) => void;

  /** Mark an execution's user prompt as steered into a running turn, or
   *  hold the mark until that user row arrives. */
  markSteered: (executionId: string, intoExecutionId: string) => void;

  /** Remove all messages. */
  clear: () => void;
}

// Coalesce per-token streaming updates so the React tree renders at
// most once per animation frame regardless of token rate. Mutations
// also propagate to peer windows via mirroredStore, so coalescing
// reduces Tauri IPC traffic too.
const pendingStreamUpdates = new Map<string, PendingStreamUpdate>();
let streamFlushHandle: number | null = null;
let pendingFlush: (() => void) | null = null;

const scheduleFrame: (cb: () => void) => number =
  typeof requestAnimationFrame === "function"
    ? (cb) => requestAnimationFrame(cb)
    : (cb) => setTimeout(cb, 16) as unknown as number;

const cancelFrame: (handle: number) => void =
  typeof cancelAnimationFrame === "function"
    ? (h) => cancelAnimationFrame(h)
    : (h) => clearTimeout(h);

export function flushPendingStreamingUpdates(): void {
  if (pendingFlush) pendingFlush();
}

export const useConversationStore = create<ConversationState>()(
  mirroredStore("conversation", (set) => ({
    messages: [],
    pendingSteered: {},

    upsertDurableMessage: (msg) =>
      set((state) =>
        applyPendingSteered(state, upsertDurableMessage(state, msg)),
      ),

    pushMessage: (msg) =>
      set((state) =>
        applyPendingSteered(state, appendConversationMessages(state, [msg])),
      ),

    pushMessages: (msgs) =>
      set((state) =>
        applyPendingSteered(state, appendConversationMessages(state, msgs)),
      ),

    upsertStreamingAssistantMessage: (
      executionId,
      content,
      createdAt,
      metadata,
    ) => {
      const text = normalizeAssistantText(content);
      if (!text) return;
      pendingStreamUpdates.set(streamIdentity(executionId, metadata?.stepId), {
        executionId,
        stepId: metadata?.stepId,
        content: text,
        createdAt,
        sessionId: metadata?.sessionId,
        conversationFrameId: metadata?.conversationFrameId,
      });
      if (streamFlushHandle !== null) return;
      const flush = () => {
        if (streamFlushHandle !== null) {
          cancelFrame(streamFlushHandle);
          streamFlushHandle = null;
        }
        pendingFlush = null;
        if (pendingStreamUpdates.size === 0) return;
        const batch = Array.from(pendingStreamUpdates.values());
        pendingStreamUpdates.clear();
        // Every window derives streaming text from the same mirrored frame.
        // Emitting this deferred whole-list write could overwrite a final
        // reply another window has already applied.
        withMutationEmitSuppressed(() =>
          set((state) => applyStreamingBatch(state, batch)),
        );
      };
      pendingFlush = flush;
      streamFlushHandle = scheduleFrame(flush);
    },

    commitStreamingAssistantProgress: (msg) => {
      if (!msg.executionId) return;
      pendingStreamUpdates.delete(streamIdentity(msg.executionId, msg.stepId));
      if (msg.stepId) {
        pendingStreamUpdates.delete(streamIdentity(msg.executionId));
      }
      set((state) => commitStreamingProgress(state, msg));
    },

    finalizeAssistantMessage: (msg) => {
      // Drop any pending coalesced update for this execution — the
      // finalized message supersedes it.
      if (msg.executionId) deletePendingStreamUpdates(msg.executionId);
      set((state) => {
        if (state.messages.some((m) => m.id === msg.id)) return state;
        const finalMessage = {
          ...normalizeConversationMessage(msg),
          isStreaming: false,
        };
        // Drop the progress copy of a streamed answer; a different last row
        // is a sentence said along the way, and stays.
        let lastProgressIndex = -1;
        for (let index = state.messages.length - 1; index >= 0; index--) {
          const message = state.messages[index];
          if (
            message?.activityKind === "progress" &&
            message.executionId === msg.executionId
          ) {
            if (sameSpokenText(message.content, finalMessage.content)) {
              lastProgressIndex = index;
            }
            break;
          }
        }
        const baseMessages =
          lastProgressIndex === -1
            ? state.messages
            : state.messages.filter((_, index) => index !== lastProgressIndex);
        const index = baseMessages.findIndex(
          (m) => m.isStreaming && m.executionId === msg.executionId,
        );
        if (index === -1) return { messages: [...baseMessages, finalMessage] };

        const messages = [...baseMessages];
        messages[index] = finalMessage;
        return { messages };
      });
    },

    reconcileUserMessage: (msg) =>
      set((state) =>
        applyPendingSteered(state, reconcileOptimisticUserMessage(state, msg)),
      ),

    clearOptimisticUserMessage: (clientRequestId) =>
      set((state) => {
        const messages = state.messages.filter(
          (message) =>
            !(
              message.isOptimistic &&
              message.clientRequestId === clientRequestId
            ),
        );
        return messages.length === state.messages.length ? state : { messages };
      }),

    rekeyOptimisticUserMessage: (clientRequestId, nextClientRequestId) =>
      set((state) => {
        const index = state.messages.findIndex(
          (message) =>
            message.isOptimistic && message.clientRequestId === clientRequestId,
        );
        const message = state.messages[index];
        if (!message) return state;
        const messages = [...state.messages];
        messages[index] = {
          ...message,
          id: `optimistic-user-${nextClientRequestId}`,
          clientRequestId: nextClientRequestId,
        };
        return { messages };
      }),

    clearStreamingAssistantMessage: (executionId) => {
      deletePendingStreamUpdates(executionId);
      set((state) => settleExecutionMessages(state, executionId));
    },

    setMessages: (msgs) => {
      const pendingBatch = Array.from(pendingStreamUpdates.values());
      pendingStreamUpdates.clear();
      if (streamFlushHandle !== null) {
        cancelFrame(streamFlushHandle);
        streamFlushHandle = null;
      }
      pendingFlush = null;
      set((state) =>
        applyPendingSteered(
          state,
          replaceConversationMessages(state, msgs, pendingBatch, false),
        ),
      );
    },

    hydrateMessages: (msgs) => {
      const pendingBatch = Array.from(pendingStreamUpdates.values());
      pendingStreamUpdates.clear();
      if (streamFlushHandle !== null) {
        cancelFrame(streamFlushHandle);
        streamFlushHandle = null;
      }
      pendingFlush = null;
      set((state) =>
        applyPendingSteered(
          state,
          replaceConversationMessages(state, msgs, pendingBatch, true),
        ),
      );
    },

    markToolFailed: (executionId, toolUseId) => {
      const id = `tool-${executionId}-${toolUseId}`;
      set((state) => {
        const index = state.messages.findIndex((message) => message.id === id);
        if (index < 0) return state;
        const message = state.messages[index]!;
        if (message.toolFailed === true) return state;
        const messages = [...state.messages];
        messages[index] = { ...message, toolFailed: true };
        return { messages };
      });
    },

    markSteered: (executionId, intoExecutionId) =>
      set((state) => {
        const isPrompt = (message: ConversationMessage) =>
          message.role === "user" && message.executionId === executionId;
        if (!state.messages.some(isPrompt))
          return {
            pendingSteered: holdSteered(
              state.pendingSteered,
              executionId,
              intoExecutionId,
            ),
          };
        if (
          state.messages.every(
            (message) =>
              !isPrompt(message) ||
              message.steeredIntoExecutionId === intoExecutionId,
          )
        )
          return state;
        return {
          messages: state.messages.map((message) =>
            isPrompt(message)
              ? { ...message, steeredIntoExecutionId: intoExecutionId }
              : message,
          ),
        };
      }),

    clear: () => {
      pendingStreamUpdates.clear();
      if (streamFlushHandle !== null) {
        cancelFrame(streamFlushHandle);
        streamFlushHandle = null;
      }
      pendingFlush = null;
      set({ messages: [], pendingSteered: {} });
    },
  })),
);

function replaceConversationMessages(
  state: { messages: ConversationMessage[] },
  msgs: ConversationMessage[],
  pendingBatch: PendingStreamUpdate[],
  preserveExisting: boolean,
): { messages: ConversationMessage[] } | typeof state {
  const existingNative = new Map(
    state.messages
      .filter((message) => message.nativeSubagentId)
      .map((message) => [message.id, message]),
  );
  const hydrated = msgs.map((message) => {
    const existing = existingNative.get(message.id);
    if (
      existing &&
      message.isStreaming &&
      (!existing.isStreaming ||
        existing.content.length > message.content.length)
    )
      return existing;
    return normalizeConversationMessage(message);
  });
  const savedAssistantExecIds = new Set(
    hydrated
      .filter((m) => m.role === "assistant" && !m.isStreaming)
      .map((m) => m.executionId)
      .filter(Boolean),
  );
  const hydratedIds = new Set(hydrated.map((m) => m.id));
  const hydratedUserRequestIds = new Set(
    hydrated
      .filter((m) => m.role === "user" && m.clientRequestId)
      .map((m) => m.clientRequestId),
  );
  const hydratedProgressSteps = new Set(
    hydrated
      .filter((m) => m.activityKind === "progress" && m.executionId && m.stepId)
      .map((m) => streamIdentity(m.executionId!, m.stepId)),
  );
  const liveDrafts = state.messages.filter(
    (m) =>
      m.isStreaming &&
      m.executionId &&
      (!!m.nativeSubagentId || !savedAssistantExecIds.has(m.executionId)) &&
      !(
        m.stepId &&
        hydratedProgressSteps.has(streamIdentity(m.executionId, m.stepId))
      ) &&
      !hydratedIds.has(m.id),
  );
  const liveSaved = preserveExisting
    ? state.messages.filter(
        (m) =>
          !m.isStreaming &&
          !hydratedIds.has(m.id) &&
          !(
            m.isOptimistic &&
            m.clientRequestId &&
            hydratedUserRequestIds.has(m.clientRequestId)
          ) &&
          !(
            m.role === "assistant" &&
            !m.nativeSubagentId &&
            m.executionId &&
            savedAssistantExecIds.has(m.executionId)
          ),
      )
    : [];
  const merged = { messages: [...hydrated, ...liveSaved, ...liveDrafts] };
  return pendingBatch.length > 0
    ? applyStreamingBatch(merged, pendingBatch)
    : merged;
}

// Receipts for executions whose user row never reaches this store (another
// conversation) must not accumulate.
const MAX_PENDING_STEERED = 20;

function holdSteered(
  pending: Record<string, string>,
  executionId: string,
  intoExecutionId: string,
): Record<string, string> {
  const entries = Object.entries(pending).filter(([id]) => id !== executionId);
  entries.push([executionId, intoExecutionId]);
  return Object.fromEntries(entries.slice(-MAX_PENDING_STEERED));
}

/** Mark user rows whose steering receipt arrived first, and drop the
 *  applied receipts. */
function applyPendingSteered(
  state: Pick<ConversationState, "messages" | "pendingSteered">,
  update: { messages: ConversationMessage[] },
): {
  messages: ConversationMessage[];
  pendingSteered?: Record<string, string>;
} {
  const pending = state.pendingSteered;
  if (update === state || Object.keys(pending).length === 0) return update;
  const remaining = { ...pending };
  const messages = update.messages.map((message) => {
    const into =
      message.role === "user" && message.executionId
        ? remaining[message.executionId]
        : undefined;
    if (!into) return message;
    delete remaining[message.executionId!];
    return { ...message, steeredIntoExecutionId: into };
  });
  if (Object.keys(remaining).length === Object.keys(pending).length)
    return update;
  return { messages, pendingSteered: remaining };
}

function deletePendingStreamUpdates(executionId: string): void {
  for (const [identity, update] of pendingStreamUpdates) {
    if (update.executionId === executionId)
      pendingStreamUpdates.delete(identity);
  }
}
